import { SendTeamsMessageForm, TeamsMessageModalScrollArea } from "@features/send-teams-message/components/SendTeamsMessageForm"
import {
    useAddToWatchlistMutation,
    useMyWatchlistQuery,
    useRemoveFromWatchlistMutation,
} from "@features/watchlist/queries"
import { ActionIcon, Box, Button, CloseButton, Group, Stack, Text, Tooltip } from "@mantine/core"
import type { GroupProps } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconArrowRight, IconBrandTeams, IconCamera, IconEyeMinus, IconEyePlus, IconEyeSearch, IconIdBadge2, IconMail, IconMessage, IconPhonePlus, IconPlus, IconPrinter, IconSettings, IconUsers } from "@tabler/icons-react"
import type { ReactNode } from "react"
import { SendConnectedAdultsEmailModal } from "./SendConnectedAdultsEmailModal"
import { Link } from "@tanstack/react-router"
import { hasPermission } from "@features/rbac/permissions"

export type StudentAction = {
    id: string
    label: string
    icon: ReactNode
    inActionBar: boolean
    onClick?: () => void
    disabled?: boolean
    loading?: boolean
}

export function useStudentActions(studentNo: string, onPrintIdCard?: () => void): StudentAction[] {
    const { data: watchlist } = useMyWatchlistQuery()
    const addToWatchlist = useAddToWatchlistMutation()
    const removeFromWatchlist = useRemoveFromWatchlistMutation()
    const studentInWatchlist = watchlist?.some((item) => item.studentNumber === studentNo) ?? false

    const openTeamsMessage = () => {
        const modalId = modals.open({
            title: 'Message student on Teams',
            children: (
                <SendTeamsMessageForm
                    defaultSelectedStudents={[studentNo]}
                    onSuccess={() => modals.close(modalId)}
                />
            ),
            size: 'lg',
            scrollAreaComponent: TeamsMessageModalScrollArea,
            styles: { content: { overflowY: 'hidden' } },
        })
    }

    const openEmail = () => {
        const modalId = modals.open({
            title: 'Send email to connected adults',
            children: <SendConnectedAdultsEmailModal studentNo={studentNo} onClose={() => modals.close(modalId)} />,
            size: 'lg',
        })
    }

    return [
        ...(hasPermission(['new-features.test']) ? [{
            id: 'email',
            label: 'Send email to student/teachers/parents',
            icon: <IconMail size={16} />,
            inActionBar: true,
            onClick: openEmail,
        }] : []),
        {
            id: 'teams-message',
            label: 'Teams message student',
            icon: <IconMessage size={16} />,
            inActionBar: true,
            onClick: openTeamsMessage,
        },
        {
            id: 'watchlist',
            label: studentInWatchlist ? 'Remove from watchlist' : 'Add to watchlist',
            icon: studentInWatchlist ? <IconEyeMinus fill="RGBA(255, 255, 255, 0.5)" size={16} /> : <IconEyePlus size={16} />,
            inActionBar: true,
            loading: addToWatchlist.isPending || removeFromWatchlist.isPending,
            onClick: () => {
                if (studentInWatchlist) {
                    removeFromWatchlist.mutate(studentNo)
                } else {
                    addToWatchlist.mutate(studentNo)
                }
            },
        },
        {
            id: 'log-parent-communication',
            label: 'Log parent communication',
            icon: <IconPhonePlus size={16} />,
            inActionBar: false,
            disabled: true,
        },
        {
            id: 'add-absence',
            label: 'Add absence',
            icon: <IconPlus size={16} />,
            inActionBar: false,
            disabled: true,
        },
        {
            id: 'view-parent-portal',
            label: 'View Parent Portal as parent',
            icon: <IconUsers size={16} />,
            inActionBar: false,
            disabled: true,
        },
        ...(onPrintIdCard && hasPermission(['id-cards.issue']) ? [{
            id: 'print-id-card',
            label: 'Print ID card',
            icon: (
                <Group gap={0}>
                    <IconPrinter size={16} />
                    <IconArrowRight size={16} />
                    <IconIdBadge2 size={16} />
                </Group>
            ),
            inActionBar: false,
            onClick: onPrintIdCard,
        }] : []),
        ...(hasPermission(['id-photos.manage']) ? [{
            id: 'take-photo',
            label: 'Take photo',
            icon: <IconCamera size={16} />,
            inActionBar: false,
            onClick: () => window.open(`https://photos.godalming.ac.uk/?group=all&user=${studentNo}`, '_blank')
        }] : []),
    ]
}

type StudentActionBarProps = {
    actions: StudentAction[]
} & GroupProps

export function StudentActionBar({ actions, ...groupProps }: StudentActionBarProps) {
    const actionBarItems = actions.filter((action) => action.inActionBar)

    if (actionBarItems.length === 0) return null

    return (
        <Group gap={4} px="xs" pb="xs" {...groupProps}>
            {actionBarItems.map((action) => (
                <Tooltip key={action.id} label={action.label} withArrow>
                    <ActionIcon
                        variant="subtle"
                        aria-label={action.label}
                        disabled={action.disabled}
                        loading={action.loading}
                        onClick={action.onClick}
                    >
                        {action.icon}
                    </ActionIcon>
                </Tooltip>
            ))}
        </Group>
    )
}

type StudentActionsProps = {
    actions: StudentAction[]
    opened: boolean
    onClose: () => void
}

export const StudentActions: React.FC<StudentActionsProps> = ({ actions, opened, onClose }) => {
    return (
        <Box
            component="aside"
            aria-hidden={!opened}
            inert={!opened ? true : undefined}
            className="h-full shrink-0 overflow-hidden"
            style={{
                width: opened ? 'min(22rem, 100%)' : 0,
                borderLeft: opened ? '1px solid var(--mantine-color-default-border)' : 0,
                backgroundColor: 'var(--mantine-color-body)',
                transition: 'width 200ms ease',
            }}
        >
            <Box w="min(22rem, 100vw)" miw="min(22rem, 100vw)" h="100%" p="md">
                <Group justify="space-between" mb="md">
                    <Text fw={600}>Actions</Text>
                    <CloseButton onClick={onClose} aria-label="Close actions panel" />
                </Group>
                <Stack gap="xs">
                    {actions.map((action) => action.id === 'print-id-card' ? (
                        <Group key={action.id} wrap="nowrap" gap={5}>
                            <Button className="grow" leftSection={action.icon} disabled={action.disabled} loading={action.loading} onClick={action.onClick}>{action.label}</Button>
                            <Tooltip label="Card printer settings"><Link to="/assets/card-printer"><ActionIcon variant="light" size="lg" aria-label="Card printer settings"><IconSettings size={18} /></ActionIcon></Link></Tooltip>
                        </Group>
                    ) : (
                        <Button key={action.id} leftSection={action.icon} disabled={action.disabled} loading={action.loading} onClick={action.onClick}>{action.label}</Button>
                    ))}
                </Stack>
            </Box>
        </Box>
    )
}
